import { inject, Injectable } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, catchError, map, tap, of } from 'rxjs';
import { SHARED_ENV } from '../tokens';
import { HttpClient, HttpRequest } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class AwsService {
  http = inject(HttpClient);
  private readonly env = inject(SHARED_ENV);
  private readonly hostUrl: string;

  constructor(private message: NzMessageService) {
    this.hostUrl = this.env.host;
  }

  downloadFile(presignedUrl: string, fileName: string, item?: any): Observable<void> {
    let filename = fileName;

    if (item && item.s3Key) {
      const keyParts = item.s3Key.split('/');
      filename = keyParts[keyParts.length - 1];
    } else if (item && item.label) {
      filename = item.label.toLowerCase().replace(/\s+/g, '-');
      const urlMatch = presignedUrl.match(/\.([a-zA-Z0-9]+)(\?|$)/);
      const extension = urlMatch ? urlMatch[1] : 'png';
      filename = `${filename}.${extension}`;
    } else {
      const urlMatch = presignedUrl.match(/\/([^\/]+\.([a-zA-Z0-9]+))(\?|$)/);
      if (urlMatch) {
        filename = urlMatch[1];
      } else {
        const extMatch = presignedUrl.match(/\.([a-zA-Z0-9]+)(\?|$)/);
        const extension = extMatch ? extMatch[1] : 'png';
        filename = `kyc-image.${extension}`;
      }
    }

    // Use HttpClient - CORS should work since https://cube-admin.ayntap.com is in AllowedOrigins
    return this.http
      .get(presignedUrl, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          if (response.body) {
            const blob = response.body;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }
        }),
        map(() => void 0),
        catchError((error) => {
          console.error('Error downloading file via HttpClient:', error);
          // Fallback: Use direct anchor tag download (bypasses CORS for downloads)
          const a = document.createElement('a');
          a.href = presignedUrl;
          a.download = filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Return success since direct download might work even with CORS
          // Note: This is a workaround - ideally CORS should be fixed on S3
          return of(void 0);
        })
      );
  }

  presign(payload: any): Observable<any> {
    return this.http.post(`${this.hostUrl}/user/s3/presign-upload`, payload);
  }

  uploadToS3Post(p: any, file: File) {
    const form = new FormData();

    Object.entries(p.fields).forEach((value) => {
      form.append(value[0] as string, value[1] as string);
    });
    form.append('file', file);

    const req = new HttpRequest('POST', p.url, form, {
      reportProgress: true,
      responseType: 'text',
    });
    return this.http.request(req);
  }

  computePublicUrlFromPost(p: any, originalName: string) {
    if (p.publicBaseUrl && (p.fields['key'] || p.key)) {
      const key = p.fields['key'] ?? p.key!;
      return this.joinUrl(p.publicBaseUrl, key);
    }
    const key = p.fields['key'] ?? p.key ?? originalName;
    return this.joinUrl(p.url, key);
  }

  private joinUrl(base: string, path: string) {
    return base.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
  }
}
